import { MaintenanceAppointmentModel, MaintenanceModel } from 'api/models';
import classNames from 'classnames/bind';
import Collapsible from 'components/Collapsible/Collapsible';
import Label from 'components/Label/Label';
import { LABEL_COLOR_TYPES } from 'components/Label/labelTypes';
import { useMaintenanceAppointmentTask } from 'maintenance/hooks/useMaintenanceAppointmentTask';
import { MAINTENACE_STATUS_MODAL_STRINGS } from 'strings/maintenance-status-modal.string';
import UserActionMaintenance from '../UserActionMaintenance/UserActionMaintenance';
import styles from './MaintenanceScheduleComponent.module.css';

const cx = classNames.bind(styles);

const getLabelFromOutcome = (
  outcome: MaintenanceAppointmentModel['outcome'],
) =>
  outcome === 'Resolved' ? (
    <Label color={LABEL_COLOR_TYPES.GREEN} text="Resolved" />
  ) : null;

const getEnding = (
  status: MaintenanceAppointmentModel['status'],
  outcome: MaintenanceAppointmentModel['outcome'],
) => {
  if (status?.toLowerCase() !== 'completed') {
    return `${status.toLowerCase()}`;
  }

  return outcome === 'NotResolved' ? 'completed.notresolved' : 'completed';
};

type MaintenanceScheduledComponentProps = {
  appointments: MaintenanceAppointmentModel[];
  maintenance: MaintenanceModel;
  showMaintenanceInfo?: boolean;
};

const MaintenanceAppointment = ({
  appointment,
  maintenance,
  showMaintenanceInfo,
}: {
  appointment: MaintenanceAppointmentModel;
  maintenance: MaintenanceModel;
  showMaintenanceInfo?: boolean;
}) => {
  // A pro and a partner are different shapes; spreading either into one binding
  // gives a union with no common members, so this names the fields actually read.
  const user: {
    name?: string;
    userId?: string;
    profileImageUrl?: string;
    logoImageUrl?: string;
  } = appointment.pro.name
    ? { ...appointment.pro }
    : { ...appointment.partner };
  const task = useMaintenanceAppointmentTask(appointment, maintenance);

  // The status suffix is built at runtime, so these lookups cannot be checked
  // against the strings object's literal keys.
  let appointmentStatusLabel =
    MAINTENACE_STATUS_MODAL_STRINGS[
      `maintenance.scheduled.status.${getEnding(appointment.status, appointment.outcome)}` as keyof typeof MAINTENACE_STATUS_MODAL_STRINGS
    ];

  if (task && showMaintenanceInfo) {
    appointmentStatusLabel = `${'summary' in task ? task.summary : task.maintenance!.summary} ${
      MAINTENACE_STATUS_MODAL_STRINGS[
        `maintenance.inHomeRepairs.scheduled.status.${getEnding(appointment.status, appointment.outcome)}` as keyof typeof MAINTENACE_STATUS_MODAL_STRINGS
      ]
    }`;
  }

  return (
    <UserActionMaintenance
      belongPro={!!appointment.pro?.name || !!appointment.partner?.name}
      name={user.name}
      time={appointment.scheduledStartOn}
      timeZone={maintenance?.home?.timeZone}
      allDay={appointment.isAllDay}
      userId={user.userId}
      profileImage={user.profileImageUrl || user.logoImageUrl}
      location={appointment.location}
      disableProfileClick
      largeTime
      taskType={
        <div
          className={cx('task-type-wrapper', {
            'handle-tags': appointment.outcome === 'Resolved',
          })}
        >
          <div>{appointmentStatusLabel}</div>
          {appointment.status === 'Completed' && (
            <div className={cx('label-wrapper')}>
              {getLabelFromOutcome(appointment.outcome)}
            </div>
          )}
        </div>
      }
    />
  );
};

export const MaintenanceScheduleComponent = ({
  appointments = [],
  maintenance,
  showMaintenanceInfo,
}: MaintenanceScheduledComponentProps) => {
  const orderedAppointments = appointments.sort(
    (a, b) =>
      new Date(b.scheduledStartOn!).getTime() -
      new Date(a.scheduledStartOn!).getTime(),
  );

  return (
    <Collapsible
      title={MAINTENACE_STATUS_MODAL_STRINGS.appointmentScheduledModal.title}
      collapsed={false}
      disabled
      inner={
        <div className="flex flex-col gap-xs pt-xs">
          {orderedAppointments.map((appointment) => {
            return (
              <MaintenanceAppointment
                key={appointment.uniqueId}
                appointment={appointment}
                maintenance={maintenance}
                showMaintenanceInfo={showMaintenanceInfo}
              />
            );
          })}
        </div>
      }
    />
  );
};
